// Подсветка терминов в PLAIN-тексте (без markdown): транскрипты аудио и
// голосовой сессии, fast-path RichContent, короткие описания.
//
// Возвращает React-узлы, а не строку с HTML: никакого dangerouslySetInnerHTML
// в пользовательском тексте.

import { createElement, Fragment, type ReactNode } from "react";
import {
	createMarkBudget,
	GLOSSARY_MARK_CLASS,
	type GlossaryIndex,
	type GlossaryMarkBudget,
	takeMark,
} from "./build-index";

/**
 * Разметить один фрагмент. `budget` общий на документ — передавай его, если
 * фрагментов несколько (иначе «первое вхождение» будет считаться поштучно).
 */
export function highlightPlainText(
	text: string,
	index: GlossaryIndex | null,
	budget?: GlossaryMarkBudget,
): ReactNode {
	if (!text) return text;
	const regex = index?.regex;
	if (!index || !regex) return text;
	const marks = budget ?? createMarkBudget();
	if (marks.used >= marks.max) return text;

	regex.lastIndex = 0;
	const parts: ReactNode[] = [];
	let cursor = 0;
	let key = 0;
	let match = regex.exec(text);

	while (match !== null) {
		const matched = match[1];
		if (matched) {
			const lower = matched.toLowerCase();
			const term = index.byLower.get(lower);
			if (term && takeMark(marks, lower)) {
				if (match.index > cursor) parts.push(text.slice(cursor, match.index));
				parts.push(
					createElement(
						"span",
						{
							key: `g${key++}`,
							"data-glossary": "true",
							"data-term": term.term,
							"data-term-id": term.id,
							className: GLOSSARY_MARK_CLASS,
						},
						matched,
					),
				);
				cursor = match.index + matched.length;
			}
		}
		if (marks.used >= marks.max) break;
		match = regex.exec(text);
	}

	if (parts.length === 0) return text;
	if (cursor < text.length) parts.push(text.slice(cursor));
	return createElement(Fragment, null, ...parts);
}

/**
 * Разметить СПИСОК фрагментов одним проходом с общим бюджетом — «первое
 * вхождение в документе» считается по всему списку, а не по каждой реплике.
 * Чистая функция: результат мемоизируется потребителем по [texts, index].
 */
export function highlightPlainTexts(
	texts: readonly string[],
	index: GlossaryIndex | null,
	max?: number,
): ReactNode[] {
	if (!index?.regex) return [...texts];
	const budget = createMarkBudget(max);
	return texts.map((text) => highlightPlainText(text, index, budget));
}
